import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Loader2, X, FileText } from "lucide-react";
import ImageUpload, { fileUrl } from "@/components/ImageUpload";
import api from "@/lib/api";

const inputCls = "w-full rounded-xl bg-surface glow-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-lime-400";

function DocUpload({ value, onChange, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const okType = /^image\//.test(f.type) || f.type === "application/pdf";
    if (!okType) { toast.error("Only images or PDF files are allowed"); e.target.value = ""; return; }
    if (f.size > 8_000_000) { toast.error("File must be under 8MB"); e.target.value = ""; return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/upload?private=true`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (err) { toast.error("Upload failed. Please try again."); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div data-testid={testid}>
      <input ref={ref} type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" data-testid={`${testid}-input`} />
      {value ? (
        <div className="flex items-center gap-2 rounded-xl glow-border px-3 py-2.5" style={{ background: "var(--surface)" }}>
          <FileText className="w-4 h-4 text-primary-c shrink-0" />
          <a href={fileUrl(value)} target="_blank" rel="noreferrer" className="text-xs truncate flex-1" style={{ color: "var(--primary-ink)" }}>View uploaded file</a>
          <button type="button" data-testid={`${testid}-remove`} onClick={() => onChange(null)} className="w-6 h-6 rounded-full glass flex items-center justify-center"><X className="w-3.5 h-3.5" style={{ color: "#ef4444" }} /></button>
        </div>
      ) : (
        <button type="button" data-testid={`${testid}-trigger`} onClick={() => ref.current?.click()} disabled={busy}
          className="w-full rounded-xl glow-border px-3 py-3 flex items-center justify-center gap-2 text-sm font-bold" style={{ background: "var(--surface)", color: "var(--text-2)" }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin text-primary-c" /> : <UploadCloud className="w-4 h-4 text-primary-c" />} {busy ? "Uploading…" : "Upload file (image / PDF)"}
        </button>
      )}
    </div>
  );
}

export default function CustomFieldInput({ field, value, onChange }) {
  if (field.type === "header") {
    return (
      <div data-testid={`cf-header-${field.id}`} className="pt-1">
        <div className="font-display font-bold text-sm">{field.label}</div>
        {field.help && <p className="text-xs mt-1" style={{ color: "var(--text-2)" }}>{field.help}</p>}
      </div>
    );
  }
  return (
    <div data-testid={`cf-field-${field.id}`}>
      <label className="text-xs uppercase font-bold tracking-wider" style={{ color: "var(--text-2)" }}>
        {field.label}{field.required && <span style={{ color: "#ef4444" }}> *</span>}
      </label>
      {field.help && <p className="text-[11px] mt-0.5" style={{ color: "var(--text-2)" }}>{field.help}</p>}
      <div className="mt-1.5">
        {field.type === "text" && <input data-testid={`cf-input-${field.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} style={{ color: "var(--text)" }} />}
        {field.type === "textarea" && <textarea data-testid={`cf-input-${field.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className={inputCls} style={{ color: "var(--text)" }} />}
        {field.type === "dropdown" && (
          <select data-testid={`cf-input-${field.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} style={{ color: "var(--text)" }}>
            <option value="">Select…</option>
            {(field.options || []).map((o, i) => <option key={i} value={o}>{o}</option>)}
          </select>
        )}
        {field.type === "image" && <ImageUpload testid={`cf-img-${field.id}`} value={value || null} onChange={(u) => onChange(u)} aspect="aspect-[3/2]" privateFile hint="Image upload" />}
        {field.type === "file" && <DocUpload testid={`cf-file-${field.id}`} value={value || null} onChange={(u) => onChange(u)} />}
      </div>
    </div>
  );
}
